import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

/**
 * Zvolená firma. Firmy jsou oddělené, takže volba platí pro celou aplikaci:
 * doklady, platby, kontakty, číselné řady i balíček pro účetní.
 *
 * `null` znamená, že firma zvolená není – shell v té chvíli pošle uživatele
 * na obrazovku výběru firmy. `'all'` je režim „Všechny firmy", který slouží
 * jen ke čtení: v něm se nedá nic vystavit ani založit.
 *
 * Volba se drží v localStorage. Přepínač „Zapamatovat" rozhoduje, jestli se
 * příště otevře rovnou naposledy použitá firma, nebo zase výběr.
 */

export type CompanyFilter = 'all' | string;
const KEY = 'isupply.company';
const REMEMBER_KEY = 'isupply.company.remember';

/** Klíče se dřív jmenovaly isupply.subject; převezmou se, ať si nikdo nemusí firmu vybírat znovu. */
function migrateLegacyKeys() {
  try {
    for (const [oldKey, newKey] of [
      ['isupply.subject', KEY],
      ['isupply.subject.remember', REMEMBER_KEY],
    ] as const) {
      const v = localStorage.getItem(oldKey);
      if (v === null) continue;
      if (localStorage.getItem(newKey) === null) localStorage.setItem(newKey, v);
      localStorage.removeItem(oldKey);
    }
  } catch {
    /* soukromé okno apod. */
  }
}

interface CompanyFilterValue {
  /** zvolená firma, `'all'` pro režim čtení, `null` když firma zvolená není */
  companyId: CompanyFilter | null;
  /** id konkrétní firmy; `null` v režimu „Všechny firmy" i bez volby */
  activeCompanyId: string | null;
  /** true v režimu „Všechny firmy" – aplikace je jen ke čtení */
  isAll: boolean;
  setCompanyId: (id: CompanyFilter) => void;
  clearCompany: () => void;
  remember: boolean;
  setRemember: (v: boolean) => void;
  /** dorazil seznam firem – zahodí volbu, která ukazuje na smazanou firmu */
  resolveDefault: (ids: string[]) => void;
  /** true, dokud se volba neporovnala se seznamem firem z API */
  resolving: boolean;
}

const Ctx = createContext<CompanyFilterValue | null>(null);

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* soukromé okno apod. */
  }
}

export function CompanyFilterProvider({ children }: { children: ReactNode }) {
  migrateLegacyKeys();
  const [remember, setRememberState] = useState(() => read(REMEMBER_KEY) !== 'ne');
  const [stored, setStored] = useState<CompanyFilter | null>(() =>
    read(REMEMBER_KEY) === 'ne' ? null : read(KEY),
  );
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    write(KEY, remember ? stored : null);
  }, [stored, remember]);

  const setCompanyId = useCallback((id: CompanyFilter) => setStored(id), []);
  const clearCompany = useCallback(() => setStored(null), []);
  const setRemember = useCallback((v: boolean) => {
    setRememberState(v);
    write(REMEMBER_KEY, v ? 'ano' : 'ne');
  }, []);

  const resolveDefault = useCallback((ids: string[]) => {
    setResolved(true);
    // firma mohla být smazaná – pak se uživatel vrátí na výběr
    setStored((cur) => (cur === 'all' || (cur && ids.includes(cur)) ? cur : null));
  }, []);

  const value = useMemo(
    () => ({
      companyId: stored,
      activeCompanyId: stored && stored !== 'all' ? stored : null,
      isAll: stored === 'all',
      setCompanyId,
      clearCompany,
      remember,
      setRemember,
      resolveDefault,
      resolving: !resolved,
    }),
    [stored, remember, setCompanyId, clearCompany, setRemember, resolveDefault, resolved],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCompanyFilter(): CompanyFilterValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useCompanyFilter mimo CompanyFilterProvider');
  return ctx;
}
